/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React, { Fragment } from 'react'

import { EuiButtonEmpty, EuiFlexGroup, EuiFlexItem, EuiTitle } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import Feature from '@modules/utils/feature'

import { isFeatureActivated } from '@/store'

import useFilters from '../useFilters'
import { Action } from '../filtersReducer'
import DateRangePicker from '../DateRangePicker'
import { maxAvailableDate, minAvailableDate } from '../constants'
import { getValidationError } from '../utils'
import ProductTypeSelect from '../ProductTypeSelect'
import SolutionSelect from '../SolutionSelect'
import { ProductType } from '../../../types'

interface Props {
  isLoading: boolean
}

const FiltersSection = ({ isLoading }: Props) => {
  const { filters, dispatch } = useFilters()
  const onResetFilters = () => dispatch({ type: Action.RESET_FILTERS })
  const shouldShowServerlessFilters = isFeatureActivated(Feature.serverless)

  return (
    <Fragment>
      <EuiFlexItem grow={false}>
        <EuiFlexGroup alignItems='center' justifyContent='spaceBetween' responsive={false}>
          <EuiFlexItem grow={false}>
            <EuiTitle size='xxs'>
              <h5>
                <FormattedMessage
                  id='billing-usage.filters.filter-section.title'
                  defaultMessage='Filter data'
                />
              </h5>
            </EuiTitle>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiButtonEmpty onClick={onResetFilters} isDisabled={isLoading}>
              <FormattedMessage id='billing-usage.filters.reset' defaultMessage='Reset' />
            </EuiButtonEmpty>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlexItem>
      <EuiFlexItem grow={false}>
        <DateRangePicker
          isDisabled={isLoading}
          selectedStartDate={filters.startDate}
          selectedEndDate={filters.endDate}
          minDate={minAvailableDate}
          maxDate={maxAvailableDate}
          onChange={(dateRange) => {
            dispatch({ type: Action.SET_DATE_RANGE, payload: dateRange })
          }}
          errorMessage={getValidationError({
            startDate: filters.startDate,
            endDate: filters.endDate,
            viewBy: filters.viewBy,
          })}
        />
      </EuiFlexItem>
      {!filters.instanceId && (
        <Fragment>
          <EuiFlexItem grow={false}>
            <ProductTypeSelect
              values={filters.productTypes}
              onChange={(productTypes) =>
                dispatch({
                  type: Action.SET_PRODUCT_TYPES,
                  payload: productTypes,
                })
              }
              isLoading={isLoading}
            />
          </EuiFlexItem>
          {shouldShowServerlessFilters && (
            <EuiFlexItem grow={false}>
              <SolutionSelect
                values={filters.solutions}
                onChange={(solutions) =>
                  dispatch({
                    type: Action.SET_SOLUTIONS,
                    payload: solutions,
                  })
                }
                isLoading={isLoading}
                isDisabled={!filters.productTypes.includes(ProductType.Projects)}
              />
            </EuiFlexItem>
          )}
        </Fragment>
      )}
    </Fragment>
  )
}

export default FiltersSection
